import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "./motion";

export const Marquee = ({ text = "TRUSTED BY FORTUNE 500 LEADERS • OVER $1.2B IN PLACED SALARIES • 98.4% RETENTION AT 24 MONTHS • EXECUTIVE SEARCH • RPO SOLUTIONS • ", fast = false }) => (
  <div className="relative overflow-hidden border-y border-slate-800/80 py-4 bg-[#0B0F19]" data-testid="marquee">
    <div className={`flex whitespace-nowrap ${fast ? "animate-marquee-fast" : "animate-marquee"}`}>
      {[0, 1].map((i) => (
        <span key={i} className="font-accent text-sm sm:text-base font-medium tracking-wide text-slate-500 flex items-center">
          {text.repeat(2).split("•").map((seg, j) => (
            <span key={j} className="flex items-center">
              <span className={j % 3 === 0 ? "text-amber-500/80 px-3" : "px-3"}>{seg.trim()}</span>
              <span className="text-amber-500">◆</span>
            </span>
          ))}
        </span>
      ))}
    </div>
  </div>
);

export const Counter = ({ value, suffix = "", duration = 1.8 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = null;
    const step = (t) => {
      if (!start) start = t;
      const p = Math.min((t - start) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.floor(eased * value));
      if (p < 1) requestAnimationFrame(step);
      else setN(value);
    };
    requestAnimationFrame(step);
  }, [inView, value, duration]);
  const display = value >= 1000 ? n.toLocaleString() : n;
  return <span ref={ref}>{display}{suffix}</span>;
};

export const PageHeader = ({ overline, title, subtitle, children }) => (
  <section className="relative pt-36 sm:pt-44 pb-14 sm:pb-20 overflow-hidden" data-testid="page-header">
    <div className="absolute top-0 right-1/4 w-[500px] h-[500px] rounded-full bg-amber-500/10 blur-[120px] animate-orb pointer-events-none" />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
      <Reveal>
        <p className="overline mb-4">{overline}</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.06] text-white max-w-4xl">{title}</h1>
        {subtitle && <p className="mt-6 text-lg sm:text-xl leading-relaxed text-slate-400 max-w-2xl">{subtitle}</p>}
        {children}
      </Reveal>
    </div>
  </section>
);

export const CTABand = () => (
  <section className="relative py-20 sm:py-28 overflow-hidden" data-testid="cta-band">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="relative rounded-3xl border border-slate-800/80 bg-gradient-to-br from-[#111827] to-[#0B0F19] p-10 sm:p-16 overflow-hidden">
        <div className="absolute -top-20 -right-20 w-[400px] h-[400px] rounded-full bg-amber-500/15 blur-[100px] animate-orb pointer-events-none" />
        <div className="relative max-w-2xl">
          <p className="overline mb-4">Let's Talk</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-tight">Ready to build your dream team?</h2>
          <p className="mt-5 text-lg text-slate-400">Let's discuss how we can help you find the right talent — or your next role.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link to="/employers" data-testid="cta-request-staff" className="group inline-flex items-center justify-center gap-1.5 px-6 py-3.5 text-sm font-semibold text-slate-950 bg-gradient-to-r from-amber-400 to-amber-500 rounded-xl shadow-lg shadow-amber-500/20 hover:shadow-amber-500/40 transition-all">
              Request Staff <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
            <Link to="/job-seekers" data-testid="cta-find-jobs" className="inline-flex items-center justify-center gap-1.5 px-6 py-3.5 text-sm font-semibold text-slate-200 border border-slate-700 rounded-xl hover:border-slate-500 hover:text-white transition-all">
              Explore Open Roles
            </Link>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export const SectionHead = ({ overline, title, subtitle, center = false }) => (
  <Reveal className={`mb-12 sm:mb-16 ${center ? "text-center mx-auto max-w-3xl" : "max-w-3xl"}`}>
    <p className="overline mb-4">{overline}</p>
    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-tight">{title}</h2>
    {subtitle && <p className="mt-5 text-lg text-slate-400 leading-relaxed">{subtitle}</p>}
  </Reveal>
);

export const LogoWall = ({ logos }) => (
  <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-6 sm:gap-x-16">
    {logos.map((l) => (
      <motion.span
        key={l}
        whileHover={{ scale: 1.05, color: "#FBBF24" }}
        className="font-accent text-lg sm:text-xl font-semibold text-slate-600 transition-colors cursor-default select-none"
      >
        {l}
      </motion.span>
    ))}
  </div>
);
